import type { QueryClient } from '@tanstack/react-query';
import { mutationOptions } from '@tanstack/react-query';
import { reviewAPIClient } from '../reviewAPIClient';
import { REVIEW_MUTATION_KEYS } from './keys';
import { REVIEW_QUERY_KEYS } from './keys';
import type {
  CreateReviewRequest,
  CreateReviewResponse,
  GetMyReviewsRequest,
  GetMyReviewsResponse,
  GetProductReviewsResponse,
  UpdateReviewRequest,
  UpdateReviewResponse,
} from './types';
import type { Review } from '../../../model';

type CachedReviewsWriter = (reviews?: Review[]) => Review[] | undefined;

/**
 * `/reviews/me` is answered per product, so a cache asking about another product must not receive
 * this review: consumers read `.at(0)` as the reader's own and would offer to edit a foreign one.
 */
const isMyReviewsCacheForProduct = (queryKey: readonly unknown[], productId: number): boolean => {
  const params = queryKey[2] as GetMyReviewsRequest | undefined;
  return params?.productId === undefined || params.productId === productId;
};

/**
 * Seeded, not refetched: the product list is edge-cached, so a refetch inside that window is
 * served the pre-submission list and drops the review that was just written.
 */
const writeToCachedReviewLists = (queryClient: QueryClient, productId: number, write: CachedReviewsWriter) => {
  queryClient.setQueriesData<GetProductReviewsResponse>(
    {
      queryKey: REVIEW_QUERY_KEYS.productScope(productId),
      // The AI rail is keyed under the same product and holds generated reviews, not this one.
      predicate: (query) => query.queryKey[3] !== 'ai',
    },
    write,
  );
  queryClient.setQueriesData<GetMyReviewsResponse>(
    {
      queryKey: REVIEW_QUERY_KEYS.myScope(),
      predicate: (query) => isMyReviewsCacheForProduct(query.queryKey, productId),
    },
    write,
  );
};

/**
 * Merged over the cached entry rather than swapped in: `verifiedPurchaser`, `repeatPurchaser` and
 * `createdAt` are populated only by `GET /reviews/product/:id`, so the edit response cannot carry
 * them. Replacing wholesale would strip the reviewer's own Verified Purchaser badge, and — since
 * cards fall back to `createdAt ?? modifiedAt` — re-date the review to the edit and jump it to the
 * top of "newest", which `modifiedAt` is specified never to do.
 */
const mergeEdit = (cached: Review, edited: Review): Review => ({
  ...cached,
  ...edited,
  verifiedPurchaser: edited.verifiedPurchaser ?? cached.verifiedPurchaser,
  repeatPurchaser: edited.repeatPurchaser ?? cached.repeatPurchaser,
  createdAt: edited.createdAt ?? cached.createdAt,
});

export const REVIEW_MUTATIONS = {
  createReview: (queryClient: QueryClient) =>
    mutationOptions<CreateReviewResponse, Error, CreateReviewRequest>({
      mutationKey: REVIEW_MUTATION_KEYS.createReview(),
      mutationFn: (params: CreateReviewRequest) => reviewAPIClient.createReview(params),
      onSuccess: (createdReview) => {
        const prependCreated: CachedReviewsWriter = (reviews) => {
          // A cache this page never read is left absent: filling it would look fetched, and the list
          // it stands for would then never load.
          if (!reviews || reviews.some((review) => review.reviewId === createdReview.reviewId)) {
            return reviews;
          }
          return [createdReview, ...reviews];
        };

        writeToCachedReviewLists(queryClient, createdReview.productId, prependCreated);

        // Posting spends the credit, so `alreadyCredited` has flipped. These are per customer and
        // uncached, so unlike the lists above they can simply be refetched.
        queryClient.invalidateQueries({ queryKey: REVIEW_QUERY_KEYS.creditEligibilityScope() });
        queryClient.invalidateQueries({ queryKey: REVIEW_QUERY_KEYS.creditSummaryScope() });
        // The rating average and count the cards carry are now one review out of date, everywhere.
        queryClient.invalidateQueries({ queryKey: REVIEW_QUERY_KEYS.statisticsScope() });
      },
    }),
  updateReview: (queryClient: QueryClient) =>
    mutationOptions<UpdateReviewResponse, Error, UpdateReviewRequest>({
      mutationKey: REVIEW_MUTATION_KEYS.updateReview(),
      mutationFn: (params: UpdateReviewRequest) => reviewAPIClient.updateReview(params),
      /**
       * An edit rewrites a review already on screen, so every cached read of it is stale. Written in
       * place rather than invalidated for the same reason a create is: refetching the edge-cached
       * product list inside its window reverts the list, and drops a review created in this session.
       */
      onSuccess: (updatedReview, variables) => {
        const applyEdit: CachedReviewsWriter = (reviews) => {
          if (!reviews?.some((review) => review.reviewId === updatedReview.reviewId)) {
            return reviews;
          }
          return reviews.map((review) =>
            review.reviewId === updatedReview.reviewId ? mergeEdit(review, updatedReview) : review,
          );
        };

        // The request, not the echo: keying off a response that omitted the product would write to
        // no cache at all and the edit would appear nowhere.
        writeToCachedReviewLists(queryClient, variables.productId, applyEdit);

        // Adding media to a text review earns the larger credit, so both verdicts can have changed.
        queryClient.invalidateQueries({ queryKey: REVIEW_QUERY_KEYS.creditEligibilityScope() });
        queryClient.invalidateQueries({ queryKey: REVIEW_QUERY_KEYS.creditSummaryScope() });
        // An edited rating moves the average the cards show.
        queryClient.invalidateQueries({ queryKey: REVIEW_QUERY_KEYS.statisticsScope() });
      },
    }),
};
